import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Light / dark / follow-the-system.
 *
 * WHY THIS IS SMALL ON PURPOSE
 * ----------------------------
 * Dark mode is normally a 2,000-class migration nobody asked for. It is cheap
 * here only because every colour already resolves through a CSS custom property,
 * so the entire palette swap is one class on <html>. There is no per-component
 * work, and there is nothing to keep in sync — which is the whole argument for
 * having built the tokens that way.
 *
 * Three states, not two. "System" is the default and it is a real setting: a
 * viewer who has told their OS they want dark should get dark without visiting
 * ours, and a viewer who has explicitly chosen light should keep light even when
 * their OS flips at sunset.
 */

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

const STORAGE_KEY = 'callvibe.theme';
export const THEMES = ['light', 'dark', 'system'];

const readStored = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(v) ? v : 'system';
  } catch {
    // Private browsing and blocked site data both throw here. Falling back to
    // 'system' is correct: it is the setting that needs no storage to work.
    return 'system';
  }
};

const systemPrefersDark = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPreference] = useState(readStored);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Track the OS setting so 'system' keeps following it after first paint.
  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return undefined;
    }
    const onChange = (e) => setSystemDark(e.matches);
    // Safari below 14 only has the deprecated listener API.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const resolved = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    // Tells the browser to theme form controls and scrollbars to match, which is
    // the difference between a dark app and a dark app with white select popups.
    root.style.colorScheme = resolved;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', resolved === 'dark' ? '#0B0E14' : '#2563EB');
  }, [resolved]);

  const setTheme = useCallback((next) => {
    if (!THEMES.includes(next)) return;
    setPreference(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice is not a reason to ignore it for
      // this session.
    }
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, isDark: resolved === 'dark', setTheme }),
    [preference, resolved, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
